/**
 * Single source of truth for the provider's CURRENT active session
 * (travelling / arrived / in-session) and its live kilometre tracking.
 *
 * WHY MODULE-LEVEL: the GPS track, live km figure, manual travel-mode override
 * and the retained check-out track (PR #399) previously lived inside
 * `useCheckInOut` — component state/refs owned by the session detail screen.
 * Any route change (Sessions → Messages → back) unmounted that screen and
 * visually reset the kilometres even though the `WebLocationTracker` singleton
 * kept watching GPS (2026-10-06 field report). This store owns all of it at
 * module level; hooks and the shell-level active-session banner subscribe via
 * `useSyncExternalStore`, so the state survives arbitrary in-app navigation.
 *
 * PERSISTENCE: snapshot + track are mirrored to localStorage (throttled for
 * sample appends) so a WebView reload mid-session resumes tracking without
 * losing the km accumulated before the reload. A process kill behaves like a
 * reload on next launch: prior samples are restored, but GPS captured while
 * the app was dead is gone (true background capture needs the native tracker —
 * see web-location-tracker.ts).
 *
 * It also drives the system-level ongoing notification (Capacitor
 * LocalNotifications) so the OS shows a Google-Maps-style "session in
 * progress" chip between check-in-ish states and check-out.
 *
 * NATIVE BACKGROUND LOCATION: the store is the single owner of the native
 * tracking lifecycle (`services/background-location.ts`). It declares the
 * desired state on every transition — `travel` while the journey is en route,
 * `session` while checked in, nothing otherwise — and the service adapter
 * reconciles (start / re-target / stop). On Android the running service owns
 * the ongoing notification, so the JS chip is suppressed while it runs.
 */

import { useSyncExternalStore } from 'react'
import { allowsDepartureAutoPrompt } from '@shared/sessions/departure-prompt-policy'
import {
  advanceGeofenceExit,
  INITIAL_GEOFENCE_EXIT_STATE,
  markGeofenceExitDismissed,
  markGeofenceExitPrompted,
  seedGeofenceExitState,
  type GeofenceExitState,
} from '@shared/sessions/geofence-exit'
import { liveProviderEtaMinutes } from '@shared/sessions/provider-eta'
import type { GeoPoint, GeoStamp, TravelModeOverride } from '@shared/sessions/types'
import { computeTrackKm } from '../features/sessions/location/track-km'
import { getLiveJourneyPublication, resetLiveJourney, subscribeLiveJourney } from './live-journey-store'
import { getLocationTracker } from '../features/sessions/location/web-location-tracker'
import {
  clearActiveSessionNotification,
  showActiveSessionNotification,
} from '../features/sessions/utils/active-session-notification'
import {
  clearBackgroundLocationPromptMemory,
  disarmNativeLivePing,
  drainBufferedPositions,
  isBackgroundLocationActive,
  onBackgroundLocationActiveChange,
  syncBackgroundLocation,
  updateBackgroundLocationNotification,
  type BackgroundLocationTarget,
  type NativeLivePingTapCredentials,
} from '../services/background-location'
import { clearNativeLivePingCredentialCache } from '../features/sessions/data/native-live-ping-credential-cache'

// NOTE: the live-tracked post-checkout `departure` phase (#422) was retired by
// the 2026-10-07 owner rule revision — departure travel is now ASSUMED equal
// to the arrival leg (or linked to the next session's arrival); see
// shared/sessions/travel-legs.ts. Tracking ends at check-out.
export type ActiveSessionPhase = 'travelling' | 'arrived' | 'inSession'

export interface ActiveSessionSnapshot {
  sessionUuid: string
  memberName: string
  phase: ActiveSessionPhase
  /** Epoch ms when the current phase began — drives the elapsed display. */
  phaseStartedAt: number
  /** True while continuous GPS capture is running for this session. */
  tracking: boolean
  /** Live accumulated billable km, or `null` when capture isn't running. */
  trackingKm: number | null
  /** Worker's "I'm driving / not driving" override for the whole track. */
  manualOverride: TravelModeOverride
  /** The member asked to cancel — the shell banner turns loud/red. */
  cancellationRequested?: boolean
  /** Geocoded session address — drives the geofence-exit departure prompt. */
  geo?: GeoPoint | null
  /**
   * Pre-shift drive estimate (ms) — the time-countdown fallback the app-wide
   * live-ping controller needs for ETA/stage when GPS cannot anchor them.
   */
  travelDurationMs?: number | null
  /**
   * Geofence-exit tracking while in session (shared/sessions/geofence-exit.ts):
   * fed by the same sample stream as the km figure, so it works with whatever
   * position source exists (foreground today, native background later).
   */
  geofenceExit?: GeofenceExitState
  /**
   * The provider's own check-in fix (coordinates only; same device-local
   * store as the km track). Anchors the live departure prompt's visit area
   * when they checked in away from the pin.
   */
  checkInPoint?: GeoPoint | null
  /**
   * False for Support Work / community-access style sessions: the live
   * departure prompt never runs (shared/sessions/departure-prompt-policy.ts).
   * Absent (older persisted snapshot) ⇒ enabled.
   */
  departurePromptEnabled?: boolean
}

const STORAGE_KEY = 'ascend-active-session-v1'
/** Trailing-edge persistence throttle for high-frequency GPS sample appends. */
const PERSIST_THROTTLE_MS = 10_000
/** A persisted active session older than this is stale (left-over) — drop it. */
const STALE_AFTER_MS = 12 * 60 * 60 * 1000
/** Min interval between km-driven updates of the system notification. */
const NOTIFY_MIN_INTERVAL_MS = 60_000

let snapshot: ActiveSessionSnapshot | null = null
let samples: GeoStamp[] = []
/**
 * Drained check-out track retained until the SERVER confirms the submit
 * (#399) AND, after a confirmed check-out, through the departure-undo window —
 * an undo must restore the track, not lose it. Released by
 * {@link releaseRetainedCheckOutTrack} when the window closes (or immediately
 * on confirm for flows with no undo, e.g. cancel).
 */
let pendingCheckOutTrack: {
  sessionUuid: string
  samples: GeoStamp[]
  manualOverride?: TravelModeOverride
  /**
   * The server CONFIRMED the check-out and the track is only being kept for
   * the departure-undo window. A retry drain must never resend it (#399's
   * no-leak invariant) — only {@link resumeActiveSessionTrackingAfterUndoCheckOut}
   * may resurrect it.
   */
  retainedForUndo?: boolean
} | null = null

const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribeActiveSession(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getActiveSessionSnapshot(): ActiveSessionSnapshot | null {
  return snapshot
}

/** React subscription to the active-session snapshot (null when none). */
export function useActiveSession(): ActiveSessionSnapshot | null {
  return useSyncExternalStore(subscribeActiveSession, getActiveSessionSnapshot, getActiveSessionSnapshot)
}

/* ------------------------------ persistence ------------------------------ */

interface PersistedState {
  snapshot: ActiveSessionSnapshot
  samples: GeoStamp[]
  pendingCheckOutTrack: {
    sessionUuid: string
    samples: GeoStamp[]
    manualOverride?: TravelModeOverride
    retainedForUndo?: boolean
  } | null
  persistedAt: number
}

function storage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

let persistTimer: ReturnType<typeof setTimeout> | null = null
let lastPersistAt = 0

function writePersisted(): void {
  const store = storage()
  if (!store) return
  lastPersistAt = Date.now()
  try {
    if (!snapshot && !pendingCheckOutTrack) {
      store.removeItem(STORAGE_KEY)
      return
    }
    if (!snapshot) {
      // Only a retained check-out track left (submit failed, session cleared):
      // keep it so a reload before the retry still has the km.
      store.setItem(
        STORAGE_KEY,
        JSON.stringify({
          snapshot: null,
          samples: [],
          pendingCheckOutTrack,
          persistedAt: lastPersistAt,
        }),
      )
      return
    }
    const state: PersistedState = { snapshot, samples, pendingCheckOutTrack, persistedAt: lastPersistAt }
    store.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* quota/serialisation issues must never break tracking itself */
  }
}

function persist(immediate: boolean): void {
  if (persistTimer != null) {
    if (!immediate) return
    clearTimeout(persistTimer)
    persistTimer = null
  }
  if (immediate || Date.now() - lastPersistAt >= PERSIST_THROTTLE_MS) {
    writePersisted()
    return
  }
  persistTimer = setTimeout(() => {
    persistTimer = null
    writePersisted()
  }, PERSIST_THROTTLE_MS - (Date.now() - lastPersistAt))
}

function hydrateFromStorage(): void {
  const store = storage()
  if (!store) return
  try {
    const raw = store.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Partial<PersistedState> & { snapshot?: ActiveSessionSnapshot | null }
    if (!parsed || typeof parsed.persistedAt !== 'number') return
    if (Date.now() - parsed.persistedAt > STALE_AFTER_MS) {
      store.removeItem(STORAGE_KEY)
      return
    }
    snapshot = parsed.snapshot ?? null
    samples = Array.isArray(parsed.samples) ? parsed.samples : []
    pendingCheckOutTrack = parsed.pendingCheckOutTrack ?? null
    // Migration: a payload persisted by the retired live-tracked-departure
    // build may carry a `departure`-phase snapshot. That phase no longer
    // exists — drop the leftover instead of resuming an unknown phase.
    if (snapshot && !['travelling', 'arrived', 'inSession'].includes(snapshot.phase)) {
      snapshot = null
      samples = []
    }
  } catch {
    /* corrupted payload — start clean */
  }
}

// Hydrate synchronously at module load so the first render after a reload is
// already correct. GPS capture is NOT restarted here (side effect) — the
// shell banner calls resumeActiveSessionTrackingIfNeeded() on mount.
hydrateFromStorage()

/* --------------------------- system notification -------------------------- */

let lastNotifiedKey = ''
let lastNotifiedAt = 0
let lastNotifiedEta: number | null = null
let lastNotifiedPhaseKey = ''

function syncNotification(immediate: boolean): void {
  if (!snapshot) {
    lastNotifiedKey = ''
    lastNotifiedEta = null
    lastNotifiedPhaseKey = ''
    void clearActiveSessionNotification()
    return
  }
  // The live SERVER ETA (the figure the journey card, the top pill and the
  // member all use) rides the native ongoing notification; a change of whole
  // minute is what re-posts it (rate-limited below like the km figure).
  const native = isBackgroundLocationActive()
  // Only the native notification carries an ETA (the iOS / JS chip never does —
  // an ETA in its key would re-post a banner every minute for nothing).
  const etaMinutes = native ? liveNotificationEtaMinutes(snapshot) : null
  const key = `${snapshot.sessionUuid}|${snapshot.phase}|${(snapshot.trackingKm ?? -1).toFixed(1)}|${
    native ? 'native' : 'js'
  }|${etaMinutes ?? 'x'}`
  if (key === lastNotifiedKey) return
  // The ETA minute changing IS the rate limit for ETA updates (≤ 1 per minute
  // of journey progress); the 60 s throttle only damps km-driven churn.
  const etaChanged = etaMinutes !== lastNotifiedEta
  if (!immediate && !etaChanged && Date.now() - lastNotifiedAt < NOTIFY_MIN_INTERVAL_MS) return
  // Tell native about the ETA only when it changed, on a phase change (which
  // clears it) or on a full re-sync — never on a km-only refresh, so native's
  // own value (updated from its ping responses while backgrounded) is not wiped.
  const phaseKey = `${snapshot.sessionUuid}|${snapshot.phase}`
  const sendEta = etaChanged || phaseKey !== lastNotifiedPhaseKey || lastNotifiedKey === ''
  lastNotifiedKey = key
  lastNotifiedAt = Date.now()
  lastNotifiedEta = etaMinutes
  lastNotifiedPhaseKey = phaseKey
  if (isBackgroundLocationActive()) {
    // The native foreground service owns the ONE ongoing notification while it
    // runs (privacy-safe generic copy + live km). Never show both.
    void clearActiveSessionNotification()
    void updateBackgroundLocationNotification({
      ...backgroundLocationCopy(snapshot),
      ...(sendEta ? { etaMinutes: etaMinutes ?? null } : {}),
    })
    return
  }
  void showActiveSessionNotification({
    sessionUuid: snapshot.sessionUuid,
    memberName: snapshot.memberName,
    phase: snapshot.phase,
    phaseStartedAt: snapshot.phaseStartedAt,
    trackingKm: snapshot.trackingKm,
  })
}

/**
 * Live server ETA for the ongoing notification: only while travelling, from the
 * journey the app-wide ping controller publishes (`null` when none is fresh —
 * the native service then shows its plain "tracking travel" title).
 */
function liveNotificationEtaMinutes(s: ActiveSessionSnapshot): number | null {
  if (s.phase !== 'travelling') return null
  const publication = getLiveJourneyPublication()
  if (!publication || publication.sessionUuid !== s.sessionUuid) return null
  return liveProviderEtaMinutes(publication.journey)
}

// A new live journey publication (every ping / backfill response the WebView
// handles) may carry a new ETA minute — refresh the notification, rate-limited.
subscribeLiveJourney(() => syncNotification(false))

/* --------------------------- native background GPS ------------------------- */

/**
 * Ongoing-notification copy for the native service. PRIVACY: never the member
 * name or address — a locked phone on a dashboard must not reveal who the
 * provider is visiting. Only the phase and the live km.
 */
function backgroundLocationCopy(s: ActiveSessionSnapshot): { title: string; body: string } {
  if (s.phase === 'travelling') {
    return {
      title: 'Tracking travel for your session',
      body: 'Your location and ETA are shared with your client and recorded for travel kilometres until you arrive.',
    }
  }
  if (s.phase === 'arrived') {
    return {
      title: 'Arrived at your session',
      body: 'Your location is recorded for travel kilometres until your arrival is recorded.',
    }
  }
  const km = s.trackingKm != null ? ` · ${s.trackingKm.toFixed(1)} km so far` : ''
  return {
    title: 'In session',
    body: `Your location is recorded for travel kilometres until you check out${km}.`,
  }
}

/**
 * Native tracking is wanted ONLY while a journey is active — travel started →
 * GPS arrival → the arrival time is recorded (`travel`; the 3-minute dwell
 * may complete with the phone locked, owner decision 2026-10-08) — or while
 * the session is checked in, arrival → departure (`session`). Never
 * always-on: every other state stops it. travelling → arrived → inSession are
 * in-place re-targets of one running service, never stop + restart.
 */
export function desiredBackgroundLocation(): BackgroundLocationTarget | null {
  if (!snapshot) return null
  const copy = backgroundLocationCopy(snapshot)
  if (snapshot.tracking) {
    return { sessionId: snapshot.sessionUuid, reason: 'session', ...copy }
  }
  if (snapshot.phase === 'travelling' || snapshot.phase === 'arrived') {
    return { sessionId: snapshot.sessionUuid, reason: 'travel', ...copy }
  }
  return null
}

function syncBackgroundLocationService(opts?: { userInitiated?: boolean }): void {
  // The travel tap's live-ping credentials ride exactly one sync (memory only).
  const livePing = pendingTapLivePing
  pendingTapLivePing = null
  void syncBackgroundLocation(desiredBackgroundLocation(), livePing ? { ...opts, livePing } : opts)
}

/** Credentials from the travel tap, consumed by the next native sync. */
let pendingTapLivePing: NativeLivePingTapCredentials | null = null
/** Session whose journey start was requested at the tap and not yet answered by the server. */
let pendingTravelStartUuid: string | null = null
const pendingTravelListeners = new Set<() => void>()

/** Single writer for the pending-start flag: notifies subscribers when it changes. */
function setPendingTravelStart(uuid: string | null): void {
  if (pendingTravelStartUuid === uuid) return
  pendingTravelStartUuid = uuid
  for (const listener of pendingTravelListeners) listener()
}

/** Reactive {@link isActiveSessionTravelStartPending} (re-renders when the tap's start is answered). */
export function useActiveSessionTravelStartPending(sessionUuid: string | null): boolean {
  return useSyncExternalStore(
    (listener) => {
      pendingTravelListeners.add(listener)
      return () => pendingTravelListeners.delete(listener)
    },
    () => sessionUuid != null && pendingTravelStartUuid === sessionUuid,
    () => false,
  )
}

// When the native service starts / stops, notification ownership flips: force
// a re-sync so exactly one ongoing notification exists at any time.
onBackgroundLocationActiveChange(() => {
  lastNotifiedKey = ''
  syncNotification(true)
})

/* -------------------------------- mutations ------------------------------- */

/** A visit (checked in / tracking) that is in progress for a session OTHER than `sessionUuid`. */
function otherSessionInProgress(
  snap: ActiveSessionSnapshot | null,
  sessionUuid: string,
): ActiveSessionSnapshot | null {
  if (!snap || snap.sessionUuid === sessionUuid) return null
  return snap.phase === 'inSession' || snap.tracking ? snap : null
}

/**
 * The in-progress visit that blocks starting travel to `sessionUuid` (owner
 * decision 2026-10-19: record the departure — or undo — for the visit in
 * progress first). Null when nothing blocks.
 */
export function getOtherSessionInProgress(
  sessionUuid: string,
): { sessionUuid: string; memberName: string } | null {
  const other = otherSessionInProgress(snapshot, sessionUuid)
  return other ? { sessionUuid: other.sessionUuid, memberName: other.memberName } : null
}

/** Reactive {@link getOtherSessionInProgress}. */
export function useOtherSessionInProgress(
  sessionUuid: string,
): { sessionUuid: string; memberName: string } | null {
  const snap = useActiveSession()
  const other = otherSessionInProgress(snap, sessionUuid)
  return other ? { sessionUuid: other.sessionUuid, memberName: other.memberName } : null
}

/**
 * Publish the journey phase for a session (from the session detail's derived
 * flow state). Re-publishing the same phase is a no-op so remounts never
 * clobber `phaseStartedAt` or the live tracking fields.
 */
export function publishActiveSessionPhase(input: {
  sessionUuid: string
  memberName: string
  phase: ActiveSessionPhase
  /** When the phase began (server timestamp when known); defaults to now. */
  phaseStartedAt?: number | null
  geo?: GeoPoint | null
  travelDurationMs?: number | null
}): void {
  // Nothing may replace an in-progress visit for a DIFFERENT session: the
  // provider records their departure (or undoes) first.
  if (otherSessionInProgress(snapshot, input.sessionUuid)) return
  const same = snapshot?.sessionUuid === input.sessionUuid
  if (same && snapshot!.phase === input.phase) {
    const needsGeo = input.geo != null && !snapshot!.geo
    const needsDuration =
      input.travelDurationMs !== undefined && input.travelDurationMs !== (snapshot!.travelDurationMs ?? null)
    if (needsGeo || needsDuration) {
      snapshot = {
        ...snapshot!,
        ...(needsGeo ? { geo: input.geo } : {}),
        ...(needsDuration ? { travelDurationMs: input.travelDurationMs ?? null } : {}),
      }
      emit()
      persist(true)
    }
    return
  }
  // Entering `travelling` for a session is the provider's own "Navigate" tap —
  // the one moment a remembered location denial may be re-prompted.
  const travelStarted = input.phase === 'travelling'
  snapshot = {
    sessionUuid: input.sessionUuid,
    memberName: input.memberName,
    phase: input.phase,
    phaseStartedAt: input.phaseStartedAt ?? Date.now(),
    tracking: same ? snapshot!.tracking : false,
    trackingKm: same ? snapshot!.trackingKm : null,
    manualOverride: same ? snapshot!.manualOverride : null,
    cancellationRequested: same ? snapshot!.cancellationRequested : false,
    geo: input.geo ?? (same ? snapshot!.geo : null) ?? null,
    travelDurationMs:
      input.travelDurationMs !== undefined
        ? input.travelDurationMs
        : same
          ? snapshot!.travelDurationMs ?? null
          : null,
    geofenceExit: same ? snapshot!.geofenceExit : INITIAL_GEOFENCE_EXIT_STATE,
  }
  if (!same) samples = []
  emit()
  persist(true)
  syncNotification(true)
  // Travel started → native capture on (journey pings keep flowing behind the
  // lock screen); stays on through `arrived` (dwell may complete while
  // locked) and re-targets in place at check-in.
  syncBackgroundLocationService({ userInitiated: travelStarted })
}

/**
 * The provider TAPPED "Navigate": start native tracking (foreground service +
 * live pings) NOW — in the same synchronous JS turn as the tap, before the
 * server answers the journey start. 2026-10-19 (build 127): arming waited on
 * the server response + a React effect, and with Android Auto / Maps on the
 * screen JS never ran again — 107 s with no FGS and no pings.
 *
 * Returns false (and does nothing) when another session is in progress — the
 * caller must then NOT start the journey.
 *
 * `livePing` is the (cached) ID-token handoff; absent when no fresh token was
 * available synchronously (native tracking still starts; the token follows via
 * `armNativeLivePing`). Pair with {@link confirmActiveSessionTravelStarted} on
 * success or {@link abortActiveSessionTravel} on failure.
 */
export function beginActiveSessionTravel(input: {
  sessionUuid: string
  memberName: string
  geo?: GeoPoint | null
  travelDurationMs?: number | null
  livePing?: NativeLivePingTapCredentials | null
}): boolean {
  // Checked in to ANOTHER session: refuse (the card shows the message and
  // never starts the journey). Nothing is begun or left pending.
  if (otherSessionInProgress(snapshot, input.sessionUuid)) return false
  setPendingTravelStart(input.sessionUuid)
  pendingTapLivePing = input.livePing ?? null
  publishActiveSessionPhase({
    sessionUuid: input.sessionUuid,
    memberName: input.memberName,
    phase: 'travelling',
    phaseStartedAt: Date.now(),
    geo: input.geo,
    travelDurationMs: input.travelDurationMs,
  })
  // Publishing the same phase again is a no-op (retried tap): sync explicitly
  // so the credentials still reach native.
  if (pendingTapLivePing) syncBackgroundLocationService({ userInitiated: true })
  return true
}

/** The server accepted the journey start — the normal flow-state publish takes over. */
export function confirmActiveSessionTravelStarted(sessionUuid: string): void {
  if (pendingTravelStartUuid === sessionUuid) setPendingTravelStart(null)
}

/** A travel start was requested at the tap and the server has not answered yet. */
export function isActiveSessionTravelStartPending(sessionUuid: string): boolean {
  return pendingTravelStartUuid === sessionUuid
}

/**
 * The server refused / failed the journey start: undo what the tap began —
 * the native live-ping credentials first, then the optimistic travelling
 * phase (which stops the foreground service). Only touches the start that is
 * still pending for this session.
 */
export function abortActiveSessionTravel(sessionUuid: string): void {
  if (pendingTravelStartUuid !== sessionUuid) return
  setPendingTravelStart(null)
  pendingTapLivePing = null
  void disarmNativeLivePing()
  if (snapshot?.sessionUuid === sessionUuid && snapshot.phase === 'travelling') {
    clearActiveSession(sessionUuid)
  }
}

/** Internal: a new GPS sample arrived from the tracker. */
function handleSample(sample: GeoStamp): void {
  if (!snapshot?.tracking) return
  samples = [...samples, sample]
  const { kmTracked } = computeTrackKm(samples, { manualOverride: snapshot.manualOverride })
  // Geofence-exit departure prompt: only meaningful while in session.
  const geofenceExit =
    snapshot.phase === 'inSession' && snapshot.departurePromptEnabled !== false
      ? advanceGeofenceExit(
          snapshot.geofenceExit ?? INITIAL_GEOFENCE_EXIT_STATE,
          sample,
          snapshot.geo,
          snapshot.checkInPoint,
        )
      : snapshot.geofenceExit
  snapshot = { ...snapshot, trackingKm: kmTracked, geofenceExit }
  emit()
  persist(false)
  syncNotification(false)
}

/**
 * Check-in succeeded for an in-person session: begin continuous GPS capture.
 * Best-effort — a capture failure never blocks the session; km simply won't
 * be tracked on this device.
 */
export function startActiveSessionTracking(session: {
  uuid: string
  memberName: string
  geo?: GeoPoint | null
  /** The check-in fix: anchors the departure-prompt area and seeds "seen inside". */
  checkIn?: GeoStamp | null
  /** Session service type — Support Work / community access never prompt. */
  serviceType?: string | null
}): void {
  const same = snapshot?.sessionUuid === session.uuid
  samples = []
  snapshot = {
    sessionUuid: session.uuid,
    memberName: session.memberName,
    phase: 'inSession',
    phaseStartedAt: Date.now(),
    tracking: true,
    trackingKm: 0,
    manualOverride: same ? snapshot!.manualOverride : null,
    geo: session.geo ?? (same ? snapshot!.geo : null) ?? null,
    geofenceExit: seedGeofenceExitState(session.checkIn),
    checkInPoint: session.checkIn
      ? { lat: session.checkIn.lat, lng: session.checkIn.lng }
      : null,
    departurePromptEnabled: allowsDepartureAutoPrompt({ serviceType: session.serviceType }),
  }
  emit()
  persist(true)
  syncNotification(true)
  void getLocationTracker()
    .startTracking(handleSample)
    .catch(() => {
      if (snapshot?.sessionUuid !== session.uuid) return
      snapshot = { ...snapshot, tracking: false, trackingKm: null }
      emit()
      persist(true)
      syncBackgroundLocationService()
    })
  // After subscribing, so fixes the service already buffered reach the track.
  // Recording the arrival is an explicit action → may re-prompt a denial.
  syncBackgroundLocationService({ userInitiated: true })
}

/**
 * Flag/unflag a pending member cancellation request on the active session so
 * the shell-level banner can surface it loudly on every route. No-op when the
 * session isn't the active one.
 */
export function setActiveSessionCancellationRequested(
  sessionUuid: string,
  requested: boolean,
): void {
  if (snapshot?.sessionUuid !== sessionUuid) return
  if ((snapshot.cancellationRequested ?? false) === requested) return
  snapshot = { ...snapshot, cancellationRequested: requested }
  emit()
  persist(true)
}

/** Set the "I'm driving / not driving" override; re-estimates live km. */
export function setActiveSessionTravelOverride(override: TravelModeOverride): void {
  if (!snapshot) return
  const trackingKm =
    samples.length > 0
      ? computeTrackKm(samples, { manualOverride: override }).kmTracked
      : snapshot.trackingKm
  snapshot = { ...snapshot, manualOverride: override, trackingKm }
  emit()
  persist(true)
}

/**
 * Check-out is being submitted: stop GPS capture and hand back the full track
 * (+ the override to classify it with). The track is RETAINED until
 * {@link confirmActiveSessionCheckOut} so a failed submit + retry — even
 * across navigation or a reload — never loses billable km (#399, hardened).
 *
 * The store's own sample buffer is canonical (it survives reloads where the
 * tracker's in-memory buffer does not); the drained tracker buffer is only a
 * fallback for tracking started outside the store.
 */
export async function drainActiveSessionTrackForCheckOut(
  sessionUuid: string,
): Promise<{ samples: GeoStamp[]; manualOverride: TravelModeOverride } | null> {
  let drained: GeoStamp[] = []
  try {
    // Fixes the native service captured while the phone was locked right up
    // to this tap belong to the billed track — pull them before stopping.
    await drainBufferedPositions()
    drained = await getLocationTracker().stopTracking()
  } catch {
    /* capture teardown is best-effort; never block check-out on it */
  }

  const storeOwnsTrack = snapshot?.sessionUuid === sessionUuid && samples.length > 0
  const track = storeOwnsTrack ? samples : drained
  const manualOverride = snapshot?.sessionUuid === sessionUuid ? snapshot.manualOverride : null

  if (snapshot?.sessionUuid === sessionUuid && snapshot.tracking) {
    snapshot = { ...snapshot, tracking: false, trackingKm: null }
  }
  samples = []
  if (track.length > 0) {
    pendingCheckOutTrack = { sessionUuid, samples: track, manualOverride }
  }
  emit()
  persist(true)
  syncBackgroundLocationService()

  // A track retained ONLY for the departure-undo window belongs to an
  // already-confirmed check-out — it must never ride along on a fresh submit.
  const retained =
    pendingCheckOutTrack &&
    pendingCheckOutTrack.sessionUuid === sessionUuid &&
    !pendingCheckOutTrack.retainedForUndo
      ? pendingCheckOutTrack
      : null
  return retained ? { samples: retained.samples, manualOverride } : null
}

/**
 * Server confirmed the check-out: clear the active session (banner +
 * notification). The retained track is released UNLESS the caller is keeping
 * it alive for the departure-undo window (`retainTrackForUndo`) — an undo
 * restores it via {@link resumeActiveSessionTrackingAfterUndoCheckOut}; the
 * undo-window owner releases it via {@link releaseRetainedCheckOutTrack} when
 * the window closes. Keeping it indefinitely would leak a stale track into a
 * future retry.
 */
export function confirmActiveSessionCheckOut(
  sessionUuid: string,
  opts?: { retainTrackForUndo?: boolean },
): void {
  if (pendingCheckOutTrack?.sessionUuid === sessionUuid) {
    pendingCheckOutTrack = opts?.retainTrackForUndo
      ? { ...pendingCheckOutTrack, retainedForUndo: true }
      : null
  }
  if (snapshot?.sessionUuid === sessionUuid) {
    snapshot = null
    samples = []
  }
  emit()
  persist(true)
  syncNotification(true)
  syncBackgroundLocationService()
}

/**
 * The departure-undo window closed without an undo (or undo became
 * impossible): drop the retained check-out track so it can never leak into a
 * future retry.
 */
export function releaseRetainedCheckOutTrack(sessionUuid: string): void {
  if (pendingCheckOutTrack?.sessionUuid !== sessionUuid) return
  pendingCheckOutTrack = null
  emit()
  persist(true)
}

/**
 * The provider undid a just-made departure record: the session is live again.
 * Restore the retained check-out track into the live buffer (billable km must
 * never be lost to an undo) and resume continuous GPS capture, exactly as if
 * the check-out had not happened.
 */
export function resumeActiveSessionTrackingAfterUndoCheckOut(session: {
  uuid: string
  memberName: string
  geo?: GeoPoint | null
  checkIn?: GeoStamp | null
  serviceType?: string | null
}): void {
  const retained =
    pendingCheckOutTrack?.sessionUuid === session.uuid ? pendingCheckOutTrack : null
  const manualOverride =
    retained?.manualOverride ??
    (snapshot?.sessionUuid === session.uuid ? snapshot.manualOverride : null)
  pendingCheckOutTrack = null
  samples = retained?.samples ?? []
  const trackingKm =
    samples.length > 0 ? computeTrackKm(samples, { manualOverride }).kmTracked : 0
  snapshot = {
    sessionUuid: session.uuid,
    memberName: session.memberName,
    phase: 'inSession',
    phaseStartedAt: Date.now(),
    tracking: true,
    trackingKm,
    manualOverride,
    geo: session.geo ?? (snapshot?.sessionUuid === session.uuid ? snapshot.geo : null) ?? null,
    // The provider undid a departure — they are still on the visit. Start a
    // fresh excursion watch so the next real exit asks again (the undone one
    // was answered, implicitly, by the undo). They were inside the visit area
    // by construction (checked in, then undid only the departure).
    geofenceExit: seedGeofenceExitState(session.checkIn),
    checkInPoint: session.checkIn ? { lat: session.checkIn.lat, lng: session.checkIn.lng } : null,
    departurePromptEnabled: allowsDepartureAutoPrompt({ serviceType: session.serviceType }),
  }
  emit()
  persist(true)
  syncNotification(true)
  void getLocationTracker()
    .startTracking(handleSample)
    .catch(() => {
      if (snapshot?.sessionUuid !== session.uuid) return
      snapshot = { ...snapshot, tracking: false, trackingKm: null }
      emit()
      persist(true)
      syncBackgroundLocationService()
    })
  syncBackgroundLocationService()
}

/**
 * A check-out hold drained the track but nothing was recorded (the departure
 * sheet was closed or abandoned, or the app reloaded mid-choice): put the
 * live visit back EXACTLY as it was before the hold — retained samples back
 * into the live buffer, GPS capture restarted — without touching the phase
 * start or the geofence-exit state. No-op (false) unless this session is
 * in-session with capture stopped; a track kept only for the departure-undo
 * window is never resurrected here. Without a saved track it only restarts
 * capture when the caller knows a hold stopped it (`afterHold`) — "in session,
 * not tracking" is also telehealth, or a capture that failed to start.
 */
export function restoreActiveSessionTrackingAfterHeldCheckOut(
  sessionUuid: string,
  opts?: { afterHold?: boolean },
): boolean {
  if (snapshot?.sessionUuid !== sessionUuid || snapshot.phase !== 'inSession' || snapshot.tracking) {
    return false
  }
  const retained =
    pendingCheckOutTrack?.sessionUuid === sessionUuid && !pendingCheckOutTrack.retainedForUndo
      ? pendingCheckOutTrack
      : null
  if (!retained && !opts?.afterHold) return false
  if (retained) {
    samples = [...retained.samples, ...samples]
    pendingCheckOutTrack = null
  }
  const trackingKm =
    samples.length > 0 ? computeTrackKm(samples, { manualOverride: snapshot.manualOverride }).kmTracked : 0
  snapshot = { ...snapshot, tracking: true, trackingKm }
  emit()
  persist(true)
  syncNotification(true)
  void getLocationTracker()
    .startTracking(handleSample)
    .catch(() => {
      if (snapshot?.sessionUuid !== sessionUuid) return
      snapshot = { ...snapshot, tracking: false, trackingKm: null }
      emit()
      persist(true)
      syncBackgroundLocationService()
    })
  syncBackgroundLocationService()
  return true
}

/**
 * The geofence-exit prompt for the current excursion was shown and answered
 * (recorded, dismissed, or the notification fired): never ask again until
 * the provider comes back inside the geofence and leaves again.
 */
export function markActiveSessionGeofenceExitPrompted(sessionUuid: string): void {
  if (snapshot?.sessionUuid !== sessionUuid) return
  const current = snapshot.geofenceExit ?? INITIAL_GEOFENCE_EXIT_STATE
  const next = markGeofenceExitPrompted(current)
  if (next === current) return
  snapshot = { ...snapshot, geofenceExit: next }
  emit()
  persist(true)
}

/**
 * "I'm still here" on the geofence-exit prompt: answered like
 * {@link markActiveSessionGeofenceExitPrompted}, and the moment is kept (and
 * persisted) so the check-out departure preview can tell the server not to
 * suggest this excursion as the departure.
 */
export function dismissActiveSessionGeofenceExit(sessionUuid: string, at = Date.now()): void {
  if (snapshot?.sessionUuid !== sessionUuid) return
  const current = snapshot.geofenceExit ?? INITIAL_GEOFENCE_EXIT_STATE
  snapshot = { ...snapshot, geofenceExit: markGeofenceExitDismissed(current, at) }
  emit()
  persist(true)
}

/**
 * The session is no longer active (checked out / completed / reverted or
 * cancelled server-side). Keeps any retained check-out track — a failed
 * submit's retry may still need it.
 */
export function clearActiveSession(sessionUuid: string): void {
  if (snapshot?.sessionUuid !== sessionUuid) return
  if (snapshot.tracking) {
    void getLocationTracker()
      .stopTracking()
      .catch(() => {})
  }
  snapshot = null
  samples = []
  emit()
  persist(true)
  syncNotification(true)
  syncBackgroundLocationService()
}

/**
 * The user signed out (or the session expired): tear down EVERYTHING that
 * belongs to the signed-in provider — the km tracker, the snapshot and its
 * persisted copy, any retained check-out track, the ongoing notification,
 * and the native background-location service (which would otherwise keep
 * recording the location of a device nobody is signed in to) together with
 * its in-memory live-ping token. Idempotent; safe to call with nothing active.
 */
export function resetActiveSessionForSignOut(): void {
  // First, before anything async: the signed-out user's ID token must not
  // stay in native memory (the service stop below also drops it).
  void disarmNativeLivePing()
  // …nor may it survive in JS memory (cached for the travel tap / queued for
  // the next native sync).
  clearNativeLivePingCredentialCache()
  pendingTapLivePing = null
  setPendingTravelStart(null)
  // The shared live-journey view (position, journey) belongs to the signed-out
  // user too — never leak it into the next account.
  resetLiveJourney()
  if (snapshot?.tracking) {
    void getLocationTracker()
      .stopTracking()
      .catch(() => {})
  }
  snapshot = null
  samples = []
  pendingCheckOutTrack = null
  if (persistTimer != null) {
    clearTimeout(persistTimer)
    persistTimer = null
  }
  storage()?.removeItem(STORAGE_KEY)
  clearBackgroundLocationPromptMemory()
  emit()
  syncNotification(true)
  void syncBackgroundLocation(null)
}

/**
 * After a WebView reload (or process restart) with a hydrated mid-session
 * snapshot: restart GPS capture so km keeps accumulating, and re-sync the
 * system notification. Called once from the shell-level banner on mount.
 *
 * With NO active session this still reconciles the native service — a
 * location foreground service orphaned by a process kill (or a stale 12 h+
 * snapshot that was just dropped) is stopped here. Never always-on.
 */
export function resumeActiveSessionTrackingIfNeeded(): void {
  if (!snapshot) {
    syncBackgroundLocationService()
    return
  }
  // Reloaded between a check-out hold and its record (departure sheet open,
  // or a failed submit): the visit is still live — restore capture with the
  // saved track rather than leaving GPS off. Never while an undo owns it.
  if (restoreActiveSessionTrackingAfterHeldCheckOut(snapshot.sessionUuid)) return
  syncNotification(true)
  if (snapshot.tracking) {
    void getLocationTracker()
      .startTracking(handleSample)
      .catch(() => {
        if (!snapshot) return
        snapshot = { ...snapshot, tracking: false, trackingKm: null }
        emit()
        persist(true)
        syncBackgroundLocationService()
      })
  }
  // After subscribing: fixes buffered natively across the reload are drained
  // straight into the restored track.
  syncBackgroundLocationService()
}

/* --------------------------------- tests ---------------------------------- */

/** TEST ONLY: wipe in-memory state (and storage unless kept). */
export function __resetActiveSessionStoreForTests(opts?: { keepStorage?: boolean }): void {
  snapshot = null
  samples = []
  pendingCheckOutTrack = null
  pendingTapLivePing = null
  setPendingTravelStart(null)
  lastNotifiedKey = ''
  lastNotifiedAt = 0
  lastNotifiedEta = null
  lastNotifiedPhaseKey = ''
  lastPersistAt = 0
  if (persistTimer != null) {
    clearTimeout(persistTimer)
    persistTimer = null
  }
  if (!opts?.keepStorage) storage()?.removeItem(STORAGE_KEY)
}

/** TEST ONLY: force any throttled persistence to flush, then re-hydrate. */
export function __rehydrateActiveSessionStoreForTests(): void {
  writePersisted()
  snapshot = null
  samples = []
  pendingCheckOutTrack = null
  hydrateFromStorage()
}
